import {
  OrgTransactionResponseDTODataItem,
  TransactionResponseDTODataItem,
} from "@/generated/api/models";
import { Spinner } from "@/components/ui/spinner";
import { VStack } from "@/components/ui/vstack";
import {
  useOrgWalletControllerGetTransactions,
  useOrgWalletControllerGetTransactionsCSV,
} from "@/generated/api/api";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Pressable } from "@/components/ui/pressable";
import { Box } from "@/components/ui/box";
import { EmptyState } from "@/screens/common/empty.state";
import { FlatList } from "@/components/ui/flat-list";
import { HStack } from "@/components/ui/hstack";
import { Download } from "lucide-react-native";
import {
  Button,
  ButtonIcon,
  ButtonSpinner,
  ButtonText,
} from "@/components/ui/button";
import { Platform } from "react-native";
import * as FileSystem from "expo-file-system";
import * as Sharing from "expo-sharing";
import { DateRange } from "@/screens/transactions/date.picker.types";
import { useTransactionListQuery } from "@/screens/transactions/useTransactionListQuery";
import { DatePickerBadge } from "@/screens/transactions/date.picker";
import { TransactionModal } from "@/screens/transactions/transaction.modal";
import { TransactionItemMini } from "@/screens/transactions/transaction.item";

export type RestaurantTransactionListMiniProps = {
  organizationId: string;
  onListChanged?: (transactions?: OrgTransactionResponseDTODataItem[]) => void;
};

export type RestaurantTransactionListMiniRef = {
  refresh: () => void;
};

export const RestaurantTransactionListMini = forwardRef<
  RestaurantTransactionListMiniRef,
  RestaurantTransactionListMiniProps
>((props, ref) => {
  const {
    data: response,
    isLoading,
    refetch,
  } = useOrgWalletControllerGetTransactions(props.organizationId, {
    limit: 3,
    position: "",
  });

  useEffect(() => {
    props.onListChanged?.(response?.data);
  }, [response]);

  useImperativeHandle(
    ref,
    () => ({
      refresh() {
        refetch();
      },
    }),
    []
  );

  const [sheetIsOpen, setSheetIsOpen] = useState(false);
  const [sheetTransaction, setSheetTransaction] =
    useState<OrgTransactionResponseDTODataItem>();

  return (
    <>
      <Box className="grow gap-4 w-full">
        {isLoading ? (
          <VStack className="w-36 h-full justify-center p-3">
            <Spinner size="large" />
          </VStack>
        ) : !response?.data.length ? (
          <EmptyState title={"No transactions yet"} variant="ghost" />
        ) : (
          response?.data.map((t) => {
            return (
              <Pressable
                key={t.id}
                className="w-full"
                onPress={() => {
                  setSheetTransaction(t);
                  setSheetIsOpen(true);
                }}
              >
                <TransactionItemMini transaction={t} />
              </Pressable>
            );
          })
        )}
      </Box>
      {sheetTransaction && (
        <TransactionModal
          transaction={sheetTransaction}
          isOpen={sheetIsOpen}
          onClose={() => setSheetIsOpen(false)}
        />
      )}
    </>
  );
});

export type RestaurantTransactionListFullProps = {
  organizationId?: string;
  onListChanged?: (
    transactions?: Array<
      OrgTransactionResponseDTODataItem | TransactionResponseDTODataItem
    >
  ) => void;
};

export type RestaurantTransactionListFullRef = {
  refresh: () => void;
};

function DownloadCSVButton({
  organizationId,
  range,
}: {
  organizationId: string;
  range: DateRange;
}) {
  const {
    refetch: download,
    isRefetching,
    isLoading,
  } = useOrgWalletControllerGetTransactionsCSV(
    organizationId,
    range
      ? {
          startTime: range[0].toISOString(),
          endTime: range[1].toISOString(),
        }
      : undefined,
    {
      query: {
        enabled: false,
      },
    }
  );

  const fileName = useMemo(() => "transactions_report.csv", []);
  const isCSVLoading = isLoading || isRefetching;

  return (
    <Button
      size="xs"
      variant="outline"
      action="secondary"
      className="h-7"
      isDisabled={isCSVLoading}
      onPress={async () => {
        const { data: blobString } = await download();

        if (!blobString) {
          return;
        }

        if (Platform.OS === "web") {
          const link = document.createElement("a");
          link.download = fileName;
          link.href = window.URL.createObjectURL(
            new Blob([blobString as unknown as string], { type: "text/csv" })
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          if (!(await Sharing.isAvailableAsync())) {
            console.error("Sharing is not available");
            return;
          }

          if (FileSystem.cacheDirectory) {
            const temporaryFileUri = `${FileSystem.cacheDirectory}/${fileName}`;
            await FileSystem.writeAsStringAsync(
              temporaryFileUri,
              blobString as unknown as string
            );
            await Sharing.shareAsync(temporaryFileUri);
          }
        }
      }}
    >
      {isCSVLoading ? (
        <ButtonSpinner />
      ) : (
        <>
          <ButtonText>CSV</ButtonText>
          <ButtonIcon as={Download} />
        </>
      )}
    </Button>
  );
}

export const RestaurantTransactionListFull = forwardRef<
  RestaurantTransactionListFullRef,
  RestaurantTransactionListFullProps
>((props, ref) => {
  const {
    infiniteQuery: {
      fetchNextPage,
      data: transactionsData,
      isFetchingNextPage,
      isLoadingError,
      hasNextPage,
      isRefetching,
      refetch,
    },
    setSelectedRange,
    selectedRange,
  } = useTransactionListQuery(props.organizationId);

  const data = useMemo(
    () =>
      transactionsData?.pages.flatMap<
        OrgTransactionResponseDTODataItem | TransactionResponseDTODataItem
      >((p) => p.data),
    [transactionsData?.pages]
  );

  useEffect(() => {
    props.onListChanged?.(data);
  }, [data]);

  useImperativeHandle(
    ref,
    () => ({
      refresh() {
        refetch();
      },
    }),
    []
  );

  const [sheetIsOpen, setSheetIsOpen] = useState(false);
  const [sheetTransaction, setSheetTransaction] = useState<
    OrgTransactionResponseDTODataItem | TransactionResponseDTODataItem
  >();

  return (
    <>
      <FlatList
        className="w-full h-full"
        contentContainerClassName="grow gap-4 px-4"
        data={data}
        keyExtractor={(info) => info.id}
        refreshing={isRefetching}
        onRefresh={refetch}
        renderItem={(info) => {
          return (
            <Pressable
              className="w-full"
              onPress={() => {
                setSheetTransaction(info.item);
                setSheetIsOpen(true);
              }}
            >
              <TransactionItemMini transaction={info.item} />
            </Pressable>
          );
        }}
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
        onEndReached={() => {
          if (hasNextPage) {
            fetchNextPage();
          }
        }}
        onEndReachedThreshold={0.5}
        stickyHeaderIndices={[0]}
        ListHeaderComponent={() =>
          (!!data?.length || !!selectedRange) && (
            <HStack className="min-h-6 w-full bg-background-0 justify-between py-4 mb-[-1rem]">
              <DatePickerBadge
                selectedRange={selectedRange}
                onRangeChange={(r) => setSelectedRange(r)}
              />
              {props.organizationId && (
                <DownloadCSVButton
                  organizationId={props.organizationId}
                  range={selectedRange}
                />
              )}
            </HStack>
          )
        }
        ListFooterComponent={() =>
          isFetchingNextPage && <Spinner size="large" />
        }
        ListEmptyComponent={() => (
          <VStack className="h-full w-full p-3">
            {data?.length === 0 ? (
              <EmptyState title="No transactions" />
            ) : isLoadingError ? (
              <EmptyState
                title="Something went wrong"
                description="Please check your internet connection and try again"
              />
            ) : (
              <Spinner size="large" />
            )}
          </VStack>
        )}
      />
      {sheetTransaction && (
        <TransactionModal
          transaction={sheetTransaction}
          isOpen={sheetIsOpen}
          onClose={() => setSheetIsOpen(false)}
        />
      )}
    </>
  );
});
